/**
 * Preferências de voz do UTILIZADOR neste browser — não da conta, não da call.
 *
 * Hoje guarda só o "premir para falar" (ligado/desligado + a tecla). Vive em
 * `localStorage` porque é uma escolha do posto de trabalho, não algo que faça
 * sentido sincronizar entre dispositivos: o teclado é diferente em cada um.
 *
 * Ler nunca rebenta: um `localStorage` bloqueado (modo privado, permissões) ou
 * um valor corrompido devolvem o default em silêncio. Uma preferência não é
 * dado crítico — falhar a lê-la não pode partir o arranque da app.
 */

export interface VoicePrefs {
  /** Micro mudo por defeito, só abre enquanto se segura a tecla. */
  pushToTalk: boolean;
  /** `KeyboardEvent.code` da tecla de fala (ex.: "Space", "KeyT"). O `code` é
   *  independente do layout — funciona igual em teclado PT, US ou AZERTY. */
  pttKey: string;
}

const KEY = "gv.voice.prefs";

export const DEFAULT_PREFS: VoicePrefs = { pushToTalk: false, pttKey: "Space" };

export function loadVoicePrefs(): VoicePrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<VoicePrefs>;
    return {
      pushToTalk: Boolean(parsed.pushToTalk),
      pttKey: typeof parsed.pttKey === "string" && parsed.pttKey ? parsed.pttKey : DEFAULT_PREFS.pttKey,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function saveVoicePrefs(prefs: VoicePrefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // Sem persistência (modo privado): a preferência vale só esta sessão. Ok.
  }
}

/** Nome legível de um `KeyboardEvent.code` para mostrar na definição. Cobre os
 *  casos comuns; para o resto, tira o prefixo técnico ("Key"/"Digit"). */
export function keyLabel(code: string): string {
  const named: Record<string, string> = {
    Space: "Espaço",
    ControlLeft: "Ctrl esq.",
    ControlRight: "Ctrl dir.",
    ShiftLeft: "Shift esq.",
    ShiftRight: "Shift dir.",
    AltLeft: "Alt esq.",
    AltRight: "Alt dir.",
    Backquote: "`",
    Enter: "Enter",
    Tab: "Tab",
  };
  if (named[code]) return named[code];
  return code.replace(/^Key/, "").replace(/^Digit/, "");
}
